import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const btn = "bg-brass text-obsidian text-sm font-medium px-4 py-2 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-40";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");

function RequestRow({ r, onHandled }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const open = r.status === "received";
  const markHandled = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/deletion-requests/${r.id}/handled`, { note });
      toast.success("Marked as handled");
      onHandled();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`deletion-request-${r.id}`} className="border-b border-[var(--cg-border)] py-4 grid md:grid-cols-[1.4fr_1fr_auto] gap-3 items-start">
      <div className="text-sm">
        <p className="text-[#F3F4F6]">{r.name || "—"} · <span className="text-[#9CA3AF]">{r.email}</span></p>
        <p className="text-xs text-[#6B7280] mt-1">
          {r.participant_type === "organizational" ? `Organizational · ${(r.organization_names || []).join(", ")}` : "Individual"}
          {" · "}{r.completed_count}/{r.session_count} assessments completed
        </p>
        <p className="text-xs text-[#6B7280] mt-1">Requested {fmt(r.created_at)}{r.notified ? ` · emailed to ${(r.notified_to || ["admin"]).join(", ")}` : " · email not sent"}</p>
      </div>
      <div className="text-xs" data-testid="deletion-request-status">
        {open ? <span className="text-terracotta">Open</span> : (
          <div className="text-[#7D9D86]">Handled {fmt(r.handled_at)}<span className="text-[#6B7280]"> by {r.handled_by}</span>
            {r.handled_note && <p className="text-[#9CA3AF] mt-1">{r.handled_note}</p>}
          </div>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-2 md:w-64">
          <input data-testid="deletion-handled-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
            className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2 text-sm text-[#F3F4F6] focus:border-brass outline-none" />
          <button data-testid="deletion-mark-handled" className={btn} disabled={busy} onClick={markHandled}>Mark as handled</button>
        </div>
      )}
    </div>
  );
}

export const BlockedAlertWarning = ({ alerts, testId }) => {
  if (!alerts?.length) return null;
  const addrs = [...new Set(alerts.map((a) => a.to))].join(", ");
  const backup = alerts.some((a) => (a.notified_to || []).length);
  return (
    <div data-testid={testId} className="rounded-xl p-4 border border-terracotta bg-[rgba(201,106,74,0.08)] text-sm">
      <p className="text-terracotta font-medium">Alert email blocked</p>
      <p className="text-[#B9BDC7] mt-1">
        {alerts.length} deletion-request alert {alerts.length === 1 ? "email" : "emails"} to {addrs} could not be sent
        ({[...new Set(alerts.map((a) => a.code))].join(", ")}). The requests are saved in the Deletion Requests tab.{" "}
        {backup ? "A backup copy was sent to celinaray1@gmail.com." : "The backup copy to celinaray1@gmail.com was not sent either."}
      </p>
    </div>
  );
};

export const DeletionRequests = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/deletion-requests").then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load deletion requests"));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-sm text-[#9CA3AF]">Loading…</p>;
  return (
    <div className="cg-card p-6" data-testid="deletion-requests-panel">
      <h3 className="font-serif text-2xl text-[#F3F4F6] mb-1">Account deletion requests</h3>
      <p className="text-sm text-[#9CA3AF] mb-4">
        Requests from participants. Nothing is deleted automatically. Marking a request as handled only records that you've dealt with it.
      </p>
      <p className="text-sm mb-4" data-testid="deletion-open-count">
        <span className="text-[#F3F4F6]">{data.open_count}</span> <span className="text-[#9CA3AF]">open</span>
      </p>
      {data.requests.length === 0 && <p className="text-sm text-[#6B7280] py-4">No requests yet.</p>}
      <div className="mb-4"><BlockedAlertWarning alerts={data.blocked_alerts} testId="deletion-blocked-alert-warning" /></div>
      {data.requests.map((r) => <RequestRow key={r.id} r={r} onHandled={load} />)}
    </div>
  );
};
